<template>
  <ScrollToSectionHelper section-id="ContactForm" :button-text="block.content.helper_text || 'Contact us'">
    <form id="ContactForm" ref='form' method='get' >
    <fieldset v-if='!sent'>

      <!-- Always include email -->
      <input type="email" v-model='formData.email' autocomplete="email" :placeholder="block.content.email_placeholder || 'Sähköpostiosoite'" required>

      <!-- Always include subject -->
      <input type="text" v-model='formData.subject' :placeholder="block.content.subject_placeholder || 'Aihe'" required>

      <!-- Dynamic custom fields -->
      <template v-for="field in customFields" :key="field.name">
        <textarea
          rows="4"
          v-model="formData[field.name]"
          :placeholder="field.name"
        />
      </template>
    </fieldset>

    <fieldset v-if='sent'>
      <label v-html='block.content.success_message || "Kiitos viestistänne!"' />
    </fieldset>

    <button v-if='!sent' v-html='block.content.submit_text || "Lähetä"' @click.prevent='submitForm()' />
    </form>
  </ScrollToSectionHelper>
</template>

<script setup>
import axios from 'axios'
import { ref, reactive, computed } from 'vue'
import ScrollToSectionHelper from '@/components/Utility/ScrollToSectionHelper.vue'

const props = defineProps({
  block: {
    type: Object,
    required: true,
    default: {}
  }
});

// Debug: Check what's in block.content
console.log('BlockEmailForm block.content:', props.block.content);
console.log('BlockEmailForm helper_text:', props.block.content?.helper_text);

const sent = ref(false);
const formData = reactive({
  email: '',
  subject: ''
});

// Get custom fields from block content
const customFields = computed(() => {
  return props.block.content.fields || [];
});

// Initialize form data for custom fields
customFields.value.forEach(field => {
  formData[field.name] = '';
});

const submitForm = function() {
  if (!sent.value) {
    // Build query string from all form data
    const params = new URLSearchParams();
    Object.keys(formData).forEach(key => {
      if (formData[key]) {
        params.append(key, formData[key]);
      }
    });

    axios.get(`/mailer?${params.toString()}`).then(function(response){
      console.log(response);
      sent.value = true;
    }).catch(function(error) {
      console.error('Form submission error:', error);
      alert('There was an error sending your message. Please try again.');
    });
  }
}
</script>

<style scoped type="text/css">
@import '@/css/breakpoints.css';

h4 {
  font-family: 'Motif';
  margin-bottom: 2rem;
  line-height: 1.0em;
  font-size: clamp(2.5rem, 6vw, 4rem);
  word-spacing: -0.015em;
  letter-spacing: -0.015em;
  -webkit-text-size-adjust: 100%;
  text-align: center;
}

.form-description {
  font-family: 'Motif';
  margin-bottom: 3rem;
  line-height: 1.2em;
  font-size: clamp(1.2rem, 3vw, 2rem);
  word-spacing: -0.015em;
  letter-spacing: -0.015em;
  text-align: center;
  opacity: 0.8;
}

form {
  color: var(--dark);
  border: 0px solid transparent;
  outline: 0px solid transparent;
  position: relative;
  text-align: center;
  max-width: 100rem;
  margin: clamp(3rem, 8vw, 6rem) auto;
  padding: 0 2rem;
}

button {
  font-size: clamp(1rem, 2vw, 1.2rem);
  font-family: inherit;
  user-select: none;
  letter-spacing: 0.035em;
  text-transform: uppercase;
  color: var(--dark);
  background: var(--light);
  border: 0px solid transparent;
  outline: 0px solid transparent;
  border-radius: 10rem;
  height: clamp(4rem, 8vw, 5rem);
  padding: 0.75rem clamp(2rem, 4vw, 3rem);
  display: inline-block;
  transition: all 0.25s ease-in-out;
  cursor: pointer;
  margin-top: 2rem;
}

button:hover {
  cursor: pointer;
  color: var(--light);
  background-color: var(--inputcolor);
}

input, textarea, label {
  font-family: 'Motif';
  font-size: clamp(1.5rem, 4vw, 2rem);
  line-height: 1;
  letter-spacing: -0.01em;
  resize: none;
}

input, textarea {
  font-family: 'Motif';
  box-sizing: border-box;
  min-width: 40%;
  margin-bottom: clamp(1rem, 3vw, 2rem);
  background: transparent;
  border: 1px solid var(--dark);
  outline: 0px solid transparent;
  font-weight: 400;
  font-style: normal;
  padding: clamp(0.75rem, 2vw, 1rem) clamp(1rem, 3vw, 2rem) clamp(1rem, 2.5vw, 1.5rem);
  transition: all 0.2s ease-in-out;
}

textarea {
  width: clamp(300px, 100%, 90vw);
  min-height: 8rem;
  resize: vertical;
  margin-left: auto;
  margin-right: auto;
}

input::placeholder, textarea::placeholder {
  color: var(--dark);
  transition: opacity 0.1s ease-in;
}

input:focus::placeholder, textarea:focus::placeholder {
  opacity: 0.1;
}

fieldset {
  border: none;
  margin: 0;
  padding: 0;
}

/* Two fields at top - email and subject - responsive */
input[type="email"],
input:nth-of-type(1) {
  width: calc(50%);
  margin-right: 1rem;
  display: inline-block;
}

input[type="text"]:nth-of-type(1),
input:nth-of-type(2) {
  width: calc(50% - 1rem);
  display: inline-block;
}

@media (--narrow) {
  input[type="email"],
  input:nth-of-type(1),
  input[type="text"]:nth-of-type(1),
  input:nth-of-type(2) {
    width: clamp(300px, 100%, 90vw);
    margin-right: auto;
    margin-left: auto;
    display: block;
  }

  form {
    padding: 0 1rem;
  }
}

/* Custom fields take full width */
input:not([type="email"]):nth-of-type(n+3),
textarea {
  display: block;
}

textarea {
  min-height: clamp(6rem, 12vw, 8rem);
}

/* Success message styling */
fieldset label {
  font-family: 'Kessler';
  font-style: italic;
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  line-height: 1.2em;
  color: var(--light);
  text-align: center;
  width: clamp(300px, 100%, 60rem);
}

</style>
